import { ComponentDefinition } from '../base-definitions'
import { BreacrumbDefinition } from '../breadcrumb-definition'
import { BaseActionBuilder, BaseActionDefinition } from '../custom-action-definitions'
import { CustomLogicDefinition } from '../custom-logic'
import { GroupStyle } from '../enums'
import { BackgroundColor } from '../enums/background-color'
import { FieldDefinition } from '../field-definitions'
import { LogicModel } from '../logic-definitions'
import { DefinitionUtils } from '../utils'
import { BaseValidationDefinition, RangeValidationDefinition } from '../validation-definitions'
import { ActionMenuBehaviour } from './action-menu-behaviour'
import { ButtonAlign } from './button-align'
import { FormGroupDefinition } from './form-group-definition'
import { FormHeaderDefinition } from './header'
import { LabelsDefinition } from './labels-definition'
import { LinkEntityDefinition } from './link-entity-definition'
import { SaveButtonVisibility } from './save-button-visibility'
import { TabDirection } from './tab-direction'
import { UIElementDefinition } from './ui-element-definition'

export class FormModel {
  public name: string
  public entityName: string
  public idml: string
  public description: string
  public hideFormHeader: boolean
  public layout: UIElementDefinition[]
  public customActions: BaseActionDefinition[]
  public fields: FieldDefinition[]
  public formEntityName: string
  public formEntityAlias: string
  public tabDirection: TabDirection
  public avoidModalClose: boolean
  public showSiteMap: boolean
  public enablePending: boolean
  public enableFieldSearch: boolean
  public conditionalRules: LogicModel[]
  public defaultRules: BaseValidationDefinition[]
  public customLogics: CustomLogicDefinition[]
  public componentDefinition?: ComponentDefinition
  public header: FormHeaderDefinition
  public canBeDeactivated: boolean
  public breadcrumb: BreacrumbDefinition
  public editForm: string
  public labels: LabelsDefinition
  public actionMenuBehaviour: ActionMenuBehaviour
  public linkEntities: LinkEntityDefinition[]
  public backgroundColor?: BackgroundColor
  public saveButtonVisibility: SaveButtonVisibility
  public buttonCancelAlign: ButtonAlign
  public buttonSaveAlign: ButtonAlign

  constructor(model: FormModel) {
    this.name = model.name
    this.entityName = model.entityName
    this.idml = model.idml
    this.description = model.description
    this.hideFormHeader = model.hideFormHeader
    this.layout = model.layout
    this.customActions = model.customActions ? this.mapCustomActions(model.customActions) : []
    this.fields = model.fields ? this.mapFormEntity(model.fields) : []
    this.formEntityName = model.formEntityName
    this.formEntityAlias = model.formEntityAlias
    this.tabDirection = model.tabDirection
    this.avoidModalClose = model.avoidModalClose
    this.showSiteMap = model.showSiteMap
    this.enablePending = model.enablePending
    this.enableFieldSearch = model.enableFieldSearch
    this.conditionalRules = model.conditionalRules ?? []
    this.defaultRules = model.defaultRules ?? []
    this.customLogics = model.customLogics ?? []
    this.componentDefinition = model.componentDefinition
    this.header = model.header
    this.canBeDeactivated = model.canBeDeactivated
    this.breadcrumb = model.breadcrumb
    this.editForm = model.editForm
    this.labels = model.labels
    this.actionMenuBehaviour = model.actionMenuBehaviour
    this.linkEntities = model.linkEntities
    this.backgroundColor = model.backgroundColor
    this.saveButtonVisibility = model.saveButtonVisibility
    this.buttonCancelAlign = model.buttonCancelAlign ?? ButtonAlign.Right
    this.buttonSaveAlign = model.buttonSaveAlign ?? ButtonAlign.Right
  }

  public getFormFieldDefinition(fieldName: string) {
    const result = this.traverseAndSearchField(fieldName, this.layout)
    return result
  }

  public getFormDefinition(name: string): UIElementDefinition {
    const res = this.traverseAndSearch(name, this.layout)
    return res
  }

  public getFieldDefinition(fieldName: string): FieldDefinition {
    const field = this.fields.find((fld) => fld.name == fieldName)

    return field as FieldDefinition
  }

  public hasCustomComponents(): boolean {
    return this.traverseAndSearchCustomUI(this.layout)
  }

  public getNumericValidation(fieldName: string): RangeValidationDefinition {
    const res = this.defaultRules.find((r) => r.field === fieldName && r.validationType === 'Range')
    return res as RangeValidationDefinition
  }

  public traverse(callback: (item: UIElementDefinition) => void) {
    this.traverseWithCallback(this.layout, callback)
  }

  public isCentered(): boolean {
    let centered = false
    this.traverse((item) => {
      const group = item as FormGroupDefinition
      if (group.type === 'Group' && group.groupStyle === GroupStyle.CenterGroup) {
        centered = true
      }
    })
    return centered
  }

  private traverseWithCallback(layout: UIElementDefinition[], callback: (item: UIElementDefinition) => void) {
    if (!layout) return false

    for (let i = 0; i < layout.length; i++) {
      const current = layout[i]
      callback(current)
      if (current.type == 'Group' || current.type == 'Tab') {
        const groupDef = current as FormGroupDefinition
        this.traverseWithCallback(groupDef.layout, callback)
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private traverseAndSearchCustomUI(layout: any[]): boolean {
    let result = false

    if (!layout) return false

    for (let i = 0; i < layout.length; i++) {
      const current = layout[i]
      if (current.type == 'Group' || current.type == 'Tab') {
        result = this.traverseAndSearchCustomUI(current.layout)
      } else if (current.type == 'Field' && current.csutomUI) {
        result = true
      }

      if (result) break
    }
    return result
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private traverseAndSearchField(fieldName: string, layout: any[]): any {
    let result = null

    if (!layout) return result

    for (let i = 0; i < layout.length; i++) {
      const current = layout[i]
      if (current.type == 'Group' || current.type == 'Tab') {
        result = this.traverseAndSearchField(fieldName, current.layout)
      } else if (current.type == 'Field' && current.name == fieldName) {
        result = current
      }

      if (result != null) break
    }
    return result
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private traverseAndSearch(name: string, layout: any[]): any {
    let result = null

    if (!layout) return result

    for (let i = 0; i < layout.length; i++) {
      const current = layout[i]
      if (current.name == name) {
        result = current
      } else if (current.type == 'Group' || current.type == 'Tab') {
        result = this.traverseAndSearch(name, current.layout)
      }

      if (result != null) break
    }
    return result
  }

  private mapCustomActions(customActions: BaseActionDefinition[]): BaseActionDefinition[] {
    const mappedActions: BaseActionDefinition[] = []
    if (customActions) {
      customActions.forEach((action) => {
        const e = BaseActionBuilder.buildAction(action)
        if (e) {
          mappedActions.push(e)
        }
      })
    }
    return mappedActions
  }

  private mapFormEntity(fields: FieldDefinition[]): FieldDefinition[] {
    return DefinitionUtils.mapFieldDefinitions(fields)
  }
}
